<template>
  <div
    class="field-label"
    :class="{ 'field-label--auto': isAutoDetected, 'field-label--changed': !!fieldChange }"
    :data-q-change="fieldChange ? changeCell : null"
    :style="{
      fontSize: '0.9rem',
      color: disabled ? 'var(--disabled-grey-color)' : 'var(--grey-color)',
      paddingBottom: '8px',
    }"
  >
    <div class="label-left">
      <span v-html="$gt(label)"></span>
      <span v-if="required && !disabled" class="required">*</span>
      <InfoButton v-if="tooltip" :tooltip="$gt(tooltip)" :disabled="disabled" />
    </div>

    <div class="label-right">
      <!-- Ausweis-Auslese (Kundenwunsch 06.10.2026): Klick = geprüft. -->
      <button
        v-if="isAutoDetected"
        type="button"
        class="auto-chip"
        :aria-label="$gt('Automatisch erkannt – bitte prüfen. Klicken, wenn geprüft.')"
        :title="$gt('Automatisch erkannt – bitte prüfen. Klicken, wenn geprüft.')"
        @click="autoDetection.confirm(fieldKey, label)"
      >
        <ScanText class="auto-chip__icon" aria-hidden="true" />
        {{ $gt("prüfen") }}
      </button>
      <!-- Kundenfeedback 24.10.2026 (IV.10): von anderen geändert seit der zuletzt gesehenen Version. -->
      <span
        v-if="fieldChange"
        class="change-chip"
        tabindex="0"
        :title="changeHint"
        :aria-label="changeHint"
        data-testid="field-change-chip"
      >
        <PencilLine class="change-chip__icon" aria-hidden="true" />
        {{ $gt("geändert") }}
      </span>
      <HistoryIndicator v-if="auditRecords.length > 0" :records="auditRecords" :onRefresh="invalidateAuditRow" />
      <v-chip v-if="markerStatus !== null" :color="markerStatus ? 'error' : 'success'" class="marker-chip">
        <component :is="markerStatus ? X : Check" class="marker-icon" />
      </v-chip>
    </div>
  </div>
</template>

<script>
import InfoButton from "@/components/buttons/InfoButton.vue";
import HistoryIndicator from "@/components/inputs/HistoryIndicator.vue";
import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { Check, PencilLine, ScanText, X } from "lucide-vue-next";
import { changeCellKey, changeTooltip } from "@/utils/questionnaireChanges";
import { watchEffect } from "vue";

export default {
  name: "FieldLabel",

  components: {
    InfoButton,
    HistoryIndicator,
    ScanText,
    PencilLine,
  },

  inject: {
    parentKey: { default: null },
    groupId: { default: null },
    getFieldHistory: { default: null },
    invalidateAuditRow: { default: null },
    // Optional: { isMarked(fieldKey, label), confirm(fieldKey, label) } – stellt
    // ein Formular mit Ausweis-Auslese bereit (1_stepClients/Expander.vue).
    // Bewusst getrennt von den Vorschau-Markern (previewValues).
    autoDetection: { default: null },
    // Kundenfeedback 24.10.2026 (IV.10): (fieldKey) => Änderung | null – vom
    // Abschnitt des Fragebogens (base/Expander.vue).
    getFieldChange: { default: null },
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    fieldKey: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      Check,
      X,
      markerStatus: null,
    };
  },

  computed: {
    isAutoDetected() {
      return !!this.autoDetection?.isMarked(this.fieldKey, this.label);
    },

    fieldChange() {
      if (!this.getFieldChange || !this.fieldKey) return null;
      return this.getFieldChange(this.fieldKey) ?? null;
    },

    changeCell() {
      const c = this.fieldChange;
      return c ? changeCellKey(c.table, c.idRow, c.field) : null;
    },

    changeHint() {
      return this.fieldChange ? changeTooltip(this.fieldChange, (t) => this.$gt(t)) : "";
    },

    auditRecords() {
      if (!this.getFieldHistory || !this.fieldKey) return [];
      return this.getFieldHistory(this.fieldKey) ?? [];
    },
  },

  created() {
    const caseStore = useCaseStore();

    // Watch for changes in parentKey, groupId, or fieldKey and update markerStatus
    watchEffect(() => {
      if (!this.parentKey || !this.groupId || !this.fieldKey) {
        this.markerStatus = null;
        return;
      }

      this.markerStatus =
        caseStore.previewValues?.[this.parentKey]?.[this.groupId]?.[this.fieldKey]?.marked ?? null;
    });
  },
};
</script>

<style scoped>
.field-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.field-label--auto {
  gap: 4px;
}

.auto-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 0 7px;
  border: 1px solid var(--q-light-started, #e0a100);
  border-radius: 999px;
  background: var(--q-light-started-bg, #fff7e0);
  color: var(--q-warn-fg, #8a4b00);
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.auto-chip:hover {
  background: #ffedb8;
}

.auto-chip:focus-visible {
  outline: 2px solid var(--q-light-started, #e0a100);
  outline-offset: 1px;
}

.auto-chip__icon {
  width: 12px;
  height: 12px;
}

.field-label--changed {
  position: relative;
}

/* Randmarkierung links neben dem Feld (Kundenfeedback 24.10.2026, IV.10). */
.field-label--changed::before {
  content: "";
  position: absolute;
  left: -10px;
  top: 2px;
  height: 64px;
  width: 3px;
  border-radius: 2px;
  background: var(--q-light-started, #e0a100);
}

.change-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 0 7px;
  border: 1px solid var(--q-light-started, #e0a100);
  border-radius: 999px;
  background: #fff;
  color: var(--q-warn-fg, #8a4b00);
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  cursor: help;
}

.change-chip:focus-visible {
  outline: 2px solid var(--q-light-started, #e0a100);
  outline-offset: 1px;
}

.change-chip__icon {
  width: 12px;
  height: 12px;
}

.field-label--flash {
  animation: field-change-flash 1.6s ease-out 1;
}

@keyframes field-change-flash {
  0% { background-color: rgba(224, 161, 0, 0.28); }
  100% { background-color: transparent; }
}

.required {
  margin-left: 2px;
  color: red;
}

.label-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.marker-chip {
  height: 20px;
  width: 20px;
  min-width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50% !important;
  padding: 0 !important;
}

.marker-icon {
  width: 14px;
  height: 14px;
  stroke-width: 2;
}
</style>
